import React, { useEffect, useState } from 'react';
import { User, Save, Lock, Eye, EyeOff, ShieldCheck, ShieldOff, Copy, MonitorSmartphone, LogOut, Globe } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { updateMyProfile, changeMyPassword, mfaSetup, mfaEnable, mfaDisable, getSessions, revokeSession, revokeOtherSessions } from '../services/api';

const parseUA = (ua = '') => {
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : null;
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Linux/.test(ua) ? 'Linux'
    : null;
  if (browser && os) return `${browser} · ${os}`;
  if (browser) return browser;
  return ua ? ua.slice(0, 45) : 'Dispositivo desconhecido';
};

const formatSessionDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};
import { validatePassword, MIN_PASSWORD_LENGTH } from '../utils/passwordPolicy';
import { toast } from 'sonner';

const ProfileDialog = ({ open, onOpenChange, initialTab = 'profile' }) => {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState('profile');

  // Profile state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // MFA state
  const [mfaData, setMfaData] = useState(null); // {otpauth_uri, secret, recovery_codes}
  const [mfaConfirmCode, setMfaConfirmCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // Sessões ativas
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await getSessions();
      setSessions(res.data.sessions || []);
    } catch (err) {
      toast.error('Erro ao carregar sessões');
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (open && tab === 'sessions') loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const handleRevokeSession = async (s) => {
    if (!window.confirm('Encerrar esta sessão? O dispositivo precisará fazer login novamente.')) return;
    setRevokingId(s.family_id);
    try {
      await revokeSession(s.family_id);
      toast.success('Sessão encerrada');
      setSessions(prev => prev.filter(x => x.family_id !== s.family_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao encerrar sessão');
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeOthers = async () => {
    if (!window.confirm('Encerrar todas as outras sessões?')) return;
    try {
      const res = await revokeOtherSessions();
      toast.success(res.data.message);
      loadSessions();
    } catch (err) {
      toast.error('Erro ao encerrar sessões');
    }
  };

  useEffect(() => {
    if (open && user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMfaData(null);
      setMfaConfirmCode('');
      setDisablePassword('');
      setDisableCode('');
      setTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab]);

  const handleStartMfa = async () => {
    setMfaLoading(true);
    try {
      const res = await mfaSetup();
      setMfaData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao iniciar cadastro do 2FA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleConfirmMfa = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    try {
      await mfaEnable(mfaConfirmCode);
      toast.success('Autenticação em duas etapas ativada!');
      setMfaData(null);
      setMfaConfirmCode('');
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    try {
      await mfaDisable(disablePassword, disableCode);
      toast.success('Autenticação em duas etapas desativada');
      setDisablePassword('');
      setDisableCode('');
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao desativar 2FA');
    } finally {
      setMfaLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText((mfaData?.recovery_codes || []).join('\n'));
    toast.success('Códigos copiados');
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSavingProfile(true);
    try {
      await updateMyProfile({ name: name.trim(), email });
      toast.success('Perfil atualizado com sucesso');
      if (refreshUser) await refreshUser();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    const v = validatePassword(newPassword);
    if (!v.ok) {
      toast.error(v.error);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('A confirmação não confere com a nova senha');
      return;
    }
    setSavingPassword(true);
    try {
      await changeMyPassword(currentPassword, newPassword);
      toast.success('Senha alterada com sucesso');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 sm:max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <User className="w-5 h-5 text-[#003366]" />
            Meu Perfil
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Atualize seus dados pessoais ou altere sua senha.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-gray-100">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-2" /> Perfil
            </TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password">
              <Lock className="w-4 h-4 mr-2" /> Senha
            </TabsTrigger>
            <TabsTrigger value="mfa" data-testid="tab-mfa">
              <ShieldCheck className="w-4 h-4 mr-2" /> 2FA
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions">
              <MonitorSmartphone className="w-4 h-4 mr-2" /> Sessões
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 pt-4">
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profile-name" className="text-gray-700">Nome completo</Label>
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="profile-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-email" className="text-gray-700">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="profile-email-input"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="bg-white border-gray-300 text-gray-700">
                  Cancelar
                </Button>
                <Button type="submit" disabled={savingProfile} className="bg-[#003366] hover:bg-[#002244]" data-testid="profile-save-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingProfile ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent value="password" className="space-y-4 pt-4">
            <form onSubmit={handleSavePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pwd-current" className="text-gray-700">Senha atual</Label>
                <div className="relative">
                  <Input
                    id="pwd-current"
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 pr-10"
                    data-testid="pwd-current-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Mostrar/Ocultar senha"
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwd-new" className="text-gray-700">Nova senha</Label>
                <div className="relative">
                  <Input
                    id="pwd-new"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 pr-10"
                    placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`}
                    data-testid="pwd-new-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Mostrar/Ocultar senha"
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwd-confirm" className="text-gray-700">Confirmar nova senha</Label>
                <Input
                  id="pwd-confirm"
                  type={showNew ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="pwd-confirm-input"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="bg-white border-gray-300 text-gray-700">
                  Cancelar
                </Button>
                <Button type="submit" disabled={savingPassword} className="bg-[#003366] hover:bg-[#002244]" data-testid="pwd-save-btn">
                  <Lock className="w-4 h-4 mr-2" />
                  {savingPassword ? 'Alterando...' : 'Alterar Senha'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent value="mfa" className="space-y-4 pt-4">
            {user?.mfa_enabled ? (
              <form onSubmit={handleDisableMfa} className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-700 text-sm" data-testid="mfa-status-enabled">
                  <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                  Autenticação em duas etapas está <strong>ativada</strong> na sua conta.
                </div>
                <p className="text-sm text-gray-500">
                  Para desativar, confirme sua senha e um código do app autenticador (ou um código de recuperação).
                </p>
                <div className="space-y-2">
                  <Label htmlFor="mfa-disable-pwd" className="text-gray-700">Senha</Label>
                  <Input
                    id="mfa-disable-pwd"
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    className="bg-white border-gray-300 text-gray-900"
                    data-testid="mfa-disable-password-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mfa-disable-code" className="text-gray-700">Código do autenticador</Label>
                  <Input
                    id="mfa-disable-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    className="bg-white border-gray-300 text-gray-900"
                    data-testid="mfa-disable-code-input"
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={mfaLoading} variant="destructive" data-testid="mfa-disable-btn">
                    <ShieldOff className="w-4 h-4 mr-2" />
                    {mfaLoading ? 'Desativando...' : 'Desativar 2FA'}
                  </Button>
                </DialogFooter>
              </form>
            ) : mfaData ? (
              <form onSubmit={handleConfirmMfa} className="space-y-4">
                <p className="text-sm text-gray-600">
                  1. Escaneie o QR code com o <strong>Google Authenticator</strong>, Microsoft Authenticator ou Authy:
                </p>
                <div className="flex justify-center p-3 bg-white border border-gray-200 rounded-md" data-testid="mfa-qrcode">
                  <QRCodeSVG value={mfaData.otpauth_uri} size={180} />
                </div>
                <p className="text-xs text-gray-500 text-center">
                  Ou digite manualmente: <code className="bg-gray-100 px-1 rounded font-mono">{mfaData.secret}</code>
                </p>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-amber-800">
                      2. Guarde seus códigos de recuperação (exibidos só agora):
                    </p>
                    <Button type="button" variant="ghost" size="sm" onClick={copyRecoveryCodes} className="h-7 text-amber-700" data-testid="mfa-copy-codes-btn">
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-1 font-mono text-xs text-amber-900" data-testid="mfa-recovery-codes">
                    {mfaData.recovery_codes.map((c) => <span key={c}>{c}</span>)}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mfa-confirm-code" className="text-gray-700">3. Digite o código de 6 dígitos para confirmar</Label>
                  <Input
                    id="mfa-confirm-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={mfaConfirmCode}
                    onChange={(e) => setMfaConfirmCode(e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 text-center tracking-[0.3em]"
                    data-testid="mfa-confirm-code-input"
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setMfaData(null)} className="bg-white border-gray-300 text-gray-700">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={mfaLoading || !mfaConfirmCode.trim()} className="bg-[#003366] hover:bg-[#002244]" data-testid="mfa-confirm-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    {mfaLoading ? 'Confirmando...' : 'Confirmar e Ativar'}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-600 text-sm" data-testid="mfa-status-disabled">
                  <ShieldOff className="w-5 h-5 flex-shrink-0" />
                  Autenticação em duas etapas está <strong>desativada</strong>.
                </div>
                <p className="text-sm text-gray-500">
                  Adicione uma camada extra de segurança: além da senha, será exigido um código temporário
                  do seu celular a cada login.
                </p>
                <DialogFooter>
                  <Button onClick={handleStartMfa} disabled={mfaLoading} className="bg-[#003366] hover:bg-[#002244]" data-testid="mfa-start-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    {mfaLoading ? 'Gerando...' : 'Ativar 2FA'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </TabsContent>

          <TabsContent value="sessions" className="space-y-3 pt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Dispositivos conectados à sua conta.
              </p>
              {sessions.filter(s => !s.is_current).length > 0 && (
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={handleRevokeOthers}
                  className="bg-white border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                  data-testid="revoke-other-sessions-btn"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1.5" /> Encerrar outras
                </Button>
              )}
            </div>
            {sessionsLoading ? (
              <div className="py-8 text-center text-sm text-gray-400">Carregando sessões...</div>
            ) : sessions.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">Nenhuma sessão ativa encontrada</div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="sessions-list">
                {sessions.map((s) => (
                  <div
                    key={s.family_id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${s.is_current ? 'bg-emerald-50/60 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}
                    data-testid={`session-item-${s.family_id}`}
                  >
                    <MonitorSmartphone className={`w-5 h-5 mt-0.5 flex-shrink-0 ${s.is_current ? 'text-emerald-600' : 'text-[#003366]'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800 truncate">{parseUA(s.user_agent)}</p>
                        {s.is_current && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" data-testid="current-session-badge">
                            Sessão atual
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Globe className="w-3 h-3" /> {s.ip || 'IP desconhecido'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Início: {formatSessionDate(s.started_at)} · Última atividade: {formatSessionDate(s.last_activity)}
                      </p>
                    </div>
                    {!s.is_current && (
                      <Button
                        type="button" variant="ghost" size="sm"
                        onClick={() => handleRevokeSession(s)}
                        disabled={revokingId === s.family_id}
                        className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 text-xs flex-shrink-0"
                        data-testid={`revoke-session-${s.family_id}`}
                      >
                        <LogOut className="w-3.5 h-3.5 mr-1" />
                        {revokingId === s.family_id ? 'Encerrando...' : 'Encerrar'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDialog;
